import { useEffect, useCallback, useState } from 'react';
import { Textfield, Button } from '@aimsos/ui';
import { ArrowLeft } from 'lucide-react';

const INITIAL_TOOL_CONFIGURE_FORM = {
  schedulingLink: '',
  orderName: '',
  description: '',
};

/**
 * Tool configure fields + back + save (no drawer). Used inside tools library drawer or standalone slideout.
 *
 * @param {{
 *   t: (key: string, opts?: object) => string,
 *   tool: object,
 *   showBackButton?: boolean,
 *   onBack?: () => void,
 *   onSave: (formData: typeof INITIAL_TOOL_CONFIGURE_FORM) => void,
 * }} props
 */
const ToolConfigureFormPanel = ({ t, tool, showBackButton = false, onBack, onSave }) => {
  const [formData, setFormData] = useState(INITIAL_TOOL_CONFIGURE_FORM);

  useEffect(() => {
    if (!tool) return undefined;
    const timer = window.setTimeout(() => {
      setFormData({
        schedulingLink: tool.settings?.schedulingLink ?? '',
        orderName: tool.settings?.orderName ?? '',
        description: tool.settings?.description ?? '',
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [tool]);

  const handleSave = useCallback(() => {
    onSave?.(formData);
  }, [formData, onSave]);

  const handleBackKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') onBack?.();
    },
    [onBack],
  );

  if (!tool) return null;

  const title = tool.title || tool.name || t('edit.tools.tool');

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showBackButton && (
        <div className="mb-4">
          <button
            type="button"
            onClick={onBack}
            onKeyDown={handleBackKeyDown}
            className="inline-flex items-center gap-2 rounded-lg p-2 text-content-secondary hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label={t('edit.tools.backToLibrary')}
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
            <span className="text-sm font-medium">{t('edit.tools.back')}</span>
          </button>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-content">{t('edit.tools.configureHeading', { title })}</h2>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
        <Textfield
          label={t('edit.tools.fieldSchedulingLink')}
          value={formData.schedulingLink}
          onChange={(v) => setFormData((s) => ({ ...s, schedulingLink: v }))}
          placeholder="https://"
        />
        <Textfield
          label={t('edit.tools.fieldOrderName')}
          value={formData.orderName}
          onChange={(v) => setFormData((s) => ({ ...s, orderName: v }))}
          placeholder=""
        />
        <Textfield
          label={t('edit.tools.fieldDescription')}
          value={formData.description}
          onChange={(v) => setFormData((s) => ({ ...s, description: v }))}
          placeholder={t('edit.tools.optional')}
        />
      </div>

      <div className="mt-6 flex justify-end border-t border-edge pt-4">
        <Button type="button" variant="primary" size="md" onClick={handleSave}>
          {t('edit.tools.save')}
        </Button>
      </div>
    </div>
  );
};

export default ToolConfigureFormPanel;
